// The seam between Entrar, the new-password screen and whatever signs an
// athlete in (SPEC-15). The screens need a handful of calls and the answers
// they can show; keeping them behind this interface lets the screens be
// tested without Firebase.

/** Why a call failed, as the screens tell the athlete. */
export type SignInFailure =
  /** Wrong password or unknown email: one answer for both, by design. */
  | "wrong-credentials"
  /** Firebase's own throttle answered. */
  | "too-many-attempts"
  | "offline"
  /** Google's email already has an account Firebase will not merge on its own. */
  | "account-exists"
  /** The reset link expired, was used, or was never one. */
  | "link-invalid"
  | "weak-password"
  | "failed";

export class SignInError extends Error {
  constructor(readonly failure: SignInFailure) {
    super(`sign-in failed: ${failure}`);
    this.name = "SignInError";
  }
}

/** Where a Google sign-in leads: the hub, Dados gerais for a new account, or nowhere when the sheet was closed. */
export type GoogleOutcome = "assessments" | "profileOnboarding" | "cancelled";

export interface SignIn {
  /** Opens the session; the email is trimmed and in lower case. Rejects with a SignInError. */
  withPassword(email: string, password: string): Promise<void>;
  /** Google's sheet, then the session; a new account gets its claim and its first name first. */
  withGoogle(): Promise<GoogleOutcome>;
  /** Asks Firebase to email a reset link. Resolves the same whether the email has an account or not. */
  sendPasswordReset(email: string): Promise<void>;
  /** Sets the new password with the code of the emailed link. */
  confirmPasswordReset(code: string, password: string): Promise<void>;
  /** Closes a session the screen gave up waiting for; never rejects. */
  signOut(): Promise<void>;
}

/** Past this, a screen gives up waiting (SPEC-15: "max 10 s"). */
export const SIGN_IN_TIMEOUT_MS = 10_000;
